import { useEffect, useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  Search,
  Download,
  FileDown,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Check,
  Minus,
  Loader2,
} from 'lucide-react'
import { format } from 'date-fns'
import { getContentList, exportListUrl, type ContentEntity } from '../../lib/api/content'
import { useExportDownload } from '../../lib/exportDownload'
import { MemberDrawer } from './MemberDrawer'
import { cn } from '../../lib/cn'

const PAGE_SIZE = 50

const SEARCH_FIELDS = ['name', 'lastName', 'contactEmail', 'phoneNumber', 'username', 'licencePlate', 'country']

const VIA_SEG = [
  { value: '', label: 'Tutti' },
  { value: 'Email', label: 'Email' },
  { value: 'Google', label: 'Google' },
  { value: 'Facebook', label: 'Facebook' },
]
const emptyFilters = { via: '', consent: '', status: '' }

/* ── value helpers ── */
type El = Record<string, unknown>
const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
function regVia(el: El): 'Email' | 'Google' | 'Facebook' {
  return el.facebookID ? 'Facebook' : el.googleID ? 'Google' : 'Email'
}
function countryFlag(c: unknown): string | null {
  const s = String(c ?? '').trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(s)) return null
  return String.fromCodePoint(...[...s].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65))
}
// Yep dates are ISO strings ("2026-06-05T12:24:39Z"); some models send epoch numbers — handle both.
function entityDate(v: unknown): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'number') {
    const ms = v < 1e12 ? v * 1000 : v
    try {
      return format(new Date(ms), 'dd/MM/yyyy')
    } catch {
      return '—'
    }
  }
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(v)
}
function toDate(v: unknown): Date | null {
  if (v == null || v === '') return null
  if (typeof v === 'number') return new Date(v < 1e12 ? v * 1000 : v)
  const d = new Date(String(v))
  return isNaN(+d) ? null : d
}
function initials(name: unknown, last: unknown): string {
  const a = String(name ?? '').trim()
  const b = String(last ?? '').trim()
  const i = (a[0] ?? '') + (b[0] ?? '')
  return i.toUpperCase() || '·'
}

/* ── small atoms ── */
function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="cv-panel px-4 py-3.5">
      <div className="text-[11px] font-bold uppercase tracking-[1.4px] text-muted">{label}</div>
      <div className="mt-1 font-display text-[24px] font-bold leading-none tracking-[-.5px] text-coal">{value}</div>
      {sub && <div className="mt-1 text-[12px] text-muted">{sub}</div>}
    </div>
  )
}
function ConsentBadge({ ok }: { ok: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[11.5px] font-semibold',
        ok ? 'bg-ok-soft text-ok' : 'bg-paper text-faint ring-1 ring-line',
      )}
    >
      {ok ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
      {ok ? 'Sì' : 'No'}
    </span>
  )
}
function StatusChip({ enabled }: { enabled: boolean }) {
  return (
    <span className={cn('gv-chip', enabled ? 'bg-ok-soft text-ok' : 'bg-venetian-soft text-venetian')}>
      <i className="h-1.5 w-1.5 rounded-full bg-current" />
      {enabled ? 'Attivo' : 'Disattivo'}
    </span>
  )
}
function Avatar({ name, last }: { name: unknown; last: unknown }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper text-[12.5px] font-bold text-slate ring-1 ring-line">
      {initials(name, last)}
    </span>
  )
}
function CountryCell({ country }: { country: unknown }) {
  const flag = countryFlag(country)
  const label = String(country ?? '').trim()
  if (!label) return <span className="text-faint">—</span>
  return (
    <span className="inline-flex items-center gap-1.5 text-slate">
      {flag && <span className="text-[15px] leading-none">{flag}</span>}
      <span className="uppercase">{label}</span>
    </span>
  )
}

// Pagination footer — shared by the table and the card list.
function Pager({
  page,
  maxPage,
  total,
  from,
  to,
  rowsInPage,
  filtering,
  busy,
  onPrev,
  onNext,
}: {
  page: number
  maxPage: number
  total: number
  from: number
  to: number
  rowsInPage: number
  filtering: boolean
  busy: boolean
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <div className="flex items-center gap-3 px-5 py-3.5 text-[12.5px] text-muted">
      <span className="tnum">
        {filtering ? `${rowsInPage} in pagina · ` : ''}
        {from.toLocaleString('it-IT')}–{to.toLocaleString('it-IT')} di {total.toLocaleString('it-IT')}
      </span>
      <div className="ml-auto flex items-center gap-1.5">
        <button
          className="rounded-md border border-line bg-card px-2 py-1 text-slate hover:border-faint disabled:opacity-40"
          onClick={onPrev}
          disabled={page === 0 || busy}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="px-1.5 tnum">
          {page + 1} / {maxPage + 1}
        </span>
        <button
          className="rounded-md border border-line bg-card px-2 py-1 text-slate hover:border-faint disabled:opacity-40"
          onClick={onNext}
          disabled={page >= maxPage || busy}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

// Compact card used on narrow widths (mobile / small tablet) in place of a table row.
function MemberCard({
  row,
  checked,
  onToggle,
  onOpen,
}: {
  row: ContentEntity
  checked: boolean
  onToggle: () => void
  onOpen: () => void
}) {
  const el = row.elements as El
  const via = regVia(el)
  return (
    <div
      onClick={onOpen}
      className={cn('cv-panel cursor-pointer p-3.5 transition-colors hover:bg-paper', checked && 'bg-verdigris-soft/40')}
    >
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="mt-1 accent-verdigris"
        />
        <Avatar name={el.name} last={el.lastName} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-semibold text-coal">
                {String(el.name ?? '')} {String(el.lastName ?? '')}
              </div>
              <div className="truncate text-[12px] text-muted">
                {String(el.contactEmail ?? '')}
                {el.phoneNumber ? ` · ${String(el.phoneNumber)}` : ''}
              </div>
            </div>
            <StatusChip enabled={isYes(el.enabled)} />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
            <CountryCell country={el.country} />
            {el.licencePlate ? (
              <span className="rounded-md border border-line bg-paper px-2 py-0.5 font-mono text-[11px] font-semibold tracking-wider text-coal">
                {String(el.licencePlate)}
              </span>
            ) : null}
            <span className="rounded-md border border-line bg-paper px-2 py-[3px] text-[10.5px] font-semibold text-slate">
              {via}
            </span>
          </div>

          <div className="mt-2.5 flex items-center justify-between gap-3 border-t border-line-soft pt-2.5 text-[12px]">
            <span className="text-muted">Registrato {entityDate(row.creationDate)}</span>
            <span className="flex items-center gap-1.5 text-muted">
              Marketing <ConsentBadge ok={isYes(el.commercialPrivacy)} />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export function MembersPage() {
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState(emptyFilters)
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [detail, setDetail] = useState<ContentEntity | null>(null)

  function patchFilter(p: Partial<typeof filters>) {
    setFilters((f) => ({ ...f, ...p }))
  }

  // searching/filtering paginates the FULL member set client-side → always start at the first page
  useEffect(() => {
    setPage(0)
  }, [search, filters])

  const list = useQuery({
    queryKey: ['members', page],
    queryFn: () => getContentList('member', { offset: page * PAGE_SIZE, limit: PAGE_SIZE, sort: 'creationDate desc' }),
    placeholderData: keepPreviousData,
  })
  // The content API has no aggregation, so the stat cards are computed client-side from a
  // single cached full scan (members are a modest set; fetched once per session).
  const everyone = useQuery({
    queryKey: ['membersAll'],
    queryFn: () => getContentList('member', { limit: 20000, sort: 'creationDate desc' }),
    staleTime: Infinity,
  })
  const stats = useMemo(() => {
    const data = everyone.data?.data
    if (!data) return null
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - 30)
    let nuovi = 0
    let esteri = 0
    let conCountry = 0
    for (const r of data) {
      const el = r.elements as El
      const created = toDate(r.creationDate)
      if (created && created >= cutoff) nuovi++
      const c = String(el.country ?? '').trim().toUpperCase()
      if (c) {
        conCountry++
        if (c !== 'IT') esteri++
      }
    }
    const n = data.length
    // % estero is over members whose nationality is known (empty country would otherwise dilute it)
    return { n, nuovi, esteri, esteriPct: conCountry ? Math.round((esteri / conCountry) * 100) : 0 }
  }, [everyone.data])

  const hasFilters = search !== '' || filters.via !== '' || filters.consent !== '' || filters.status !== ''

  // When searching/filtering, work over the FULL member set (already loaded by `everyone` for the
  // stat cards) and paginate client-side — so search covers ALL utenti, not just the loaded page.
  const filteredAll = useMemo(() => {
    if (!hasFilters) return null
    let r = everyone.data?.data ?? []
    const q = search.trim().toLowerCase()
    // Every WORD must appear in SOME field, rather than one field containing the whole phrase:
    // name and lastName are separate fields, so searching "Marco Petteno" matched nothing while
    // the surname alone matched everyone.
    if (q) {
      const words = q.split(/\s+/).filter(Boolean)
      r = r.filter((row) =>
        words.every((w) => SEARCH_FIELDS.some((f) => String(row.elements[f] ?? '').toLowerCase().includes(w))),
      )
    }
    if (filters.via) r = r.filter((row) => regVia(row.elements as El) === filters.via)
    if (filters.consent) r = r.filter((row) => (filters.consent === 'yes') === isYes((row.elements as El).commercialPrivacy))
    if (filters.status) r = r.filter((row) => (filters.status === 'active') === isYes((row.elements as El).enabled))
    return r
  }, [hasFilters, everyone.data, search, filters])

  const total = hasFilters ? filteredAll?.length ?? 0 : list.data?.totalCount ?? 0
  const rows = hasFilters
    ? (filteredAll ?? []).slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    : list.data?.data ?? []
  // loading/refetch state follows whichever source is in play (full set vs. paged list)
  const loading = hasFilters ? everyone.isLoading : list.isLoading
  const busy = hasFilters ? everyone.isFetching : list.isFetching
  const isError = hasFilters ? everyone.isError : list.isError

  const from = total === 0 ? 0 : page * PAGE_SIZE + 1
  const to = Math.min((page + 1) * PAGE_SIZE, total)
  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1)

  function toggleRow(id: string) {
    setSelected((s) => {
      const n = new Set(s)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }
  const pageIds = rows.map((r) => r.id)
  const allChecked = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  function toggleAll() {
    setSelected((s) => {
      const n = new Set(s)
      if (allChecked) pageIds.forEach((id) => n.delete(id))
      else pageIds.forEach((id) => n.add(id))
      return n
    })
  }
  // Twelve thousand members take a moment to build server-side; the button says so.
  const exportFile = useExportDownload()

  function exportAll() {
    void exportFile.run(exportListUrl('member', { sort: 'creationDate desc' }), 'iscritti.xlsx')
  }
  function exportSelected() {
    if (selected.size === 0) return
    const ids = [...selected].map((id) => `'${id}'`).join(', ')
    void exportFile.run(exportListUrl('member', { filter: `id in (${ids})` }), 'iscritti.xlsx')
  }

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
              Utenti registrati
            </h2>
            <span className="rounded-full bg-paper px-3 py-1 text-[12.5px] font-semibold tnum text-muted ring-1 ring-line">
              {total.toLocaleString('it-IT')} utenti
            </span>
          </div>
          <p className="mt-1.5 text-[13.5px] text-muted">
            Clienti con account sul sito — anagrafica, nazionalità, provenienza dell&apos;iscrizione e consensi privacy.
          </p>
        </div>
        <div className="flex w-full items-center gap-2.5 sm:ml-auto sm:w-auto">
          {selected.size > 0 && (
            <button
              onClick={exportSelected}
              disabled={exportFile.busy}
              className="inline-flex items-center gap-2 rounded-lg border border-line bg-card px-4 py-[10px] text-[13.5px] font-semibold text-slate hover:border-faint disabled:opacity-60"
            >
              {exportFile.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {exportFile.busy ? exportFile.label : `Selezionati (${selected.size})`}
            </button>
          )}
          <button
            onClick={exportAll}
            disabled={exportFile.busy}
            className="inline-flex items-center gap-2 rounded-lg bg-verdigris px-4 py-[10px] text-[13.5px] font-semibold text-white shadow-[0_4px_14px_-6px_rgba(225,2,27,.55)] hover:bg-verdigris-700 disabled:opacity-60"
          >
            {exportFile.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exportFile.busy ? exportFile.label : `Esporta tutti${total ? ` (${total.toLocaleString('it-IT')})` : ''}`}
          </button>
        </div>
      </div>

      {/* stat cards — computed from the full member scan (all real, members-only figures) */}
      <div className="mb-[18px] grid grid-cols-2 gap-[14px] lg:grid-cols-3">
        <Stat label="Utenti totali" value={total.toLocaleString('it-IT')} sub="account registrati" />
        <Stat
          label="Nuovi (30 gg)"
          value={stats ? stats.nuovi.toLocaleString('it-IT') : '…'}
          sub="ultimi 30 giorni"
        />
        <Stat
          label="Clienti esteri"
          value={stats ? `${stats.esteriPct}%` : '…'}
          sub="nazionalità ≠ Italia"
        />
      </div>

      {/* filters */}
      <div className="mb-[18px] flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
          <input
            className="w-full rounded-lg border border-line bg-card py-[9px] pl-9 pr-3 text-[13.5px] text-slate outline-none placeholder:text-faint focus:border-verdigris"
            placeholder="Cerca per nome, email, telefono, targa…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* segmented registration source */}
        <div className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
          {VIA_SEG.map((s) => (
            <button
              key={s.label}
              onClick={() => patchFilter({ via: s.value })}
              className={cn(
                'px-3.5 py-2 text-[13px] font-semibold transition-colors',
                filters.via === s.value ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>

        <select
          className="min-w-0 flex-1 rounded-lg border border-line bg-card px-3 py-[9px] text-[13.5px] text-slate outline-none focus:border-verdigris sm:flex-none"
          value={filters.consent}
          onChange={(e) => patchFilter({ consent: e.target.value })}
        >
          <option value="">Marketing: tutti</option>
          <option value="yes">Con consenso</option>
          <option value="no">Senza consenso</option>
        </select>
        <select
          className="min-w-0 flex-1 rounded-lg border border-line bg-card px-3 py-[9px] text-[13.5px] text-slate outline-none focus:border-verdigris sm:flex-none"
          value={filters.status}
          onChange={(e) => patchFilter({ status: e.target.value })}
        >
          <option value="">Stato: tutti</option>
          <option value="active">Attivi</option>
          <option value="inactive">Disattivi</option>
        </select>

        {hasFilters && (
          <button
            onClick={() => {
              setSearch('')
              setFilters(emptyFilters)
            }}
            className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-muted hover:bg-card hover:text-coal"
          >
            <X className="h-4 w-4" /> Azzera
          </button>
        )}
      </div>

      {hasFilters && (
        <p className="mb-2.5 text-[12px] text-muted">
          I filtri si applicano agli utenti nella pagina corrente. Usa “Esporta Excel” per l&apos;elenco completo.
        </p>
      )}

      {/* list — container-query responsive: cards when narrow, table when wide */}
      <div className="mbr-cq">
        {/* narrow (mobile / small tablet): card list */}
        <div className="mbr-cards space-y-2.5">
          {isError ? (
            <div className="cv-panel px-4 py-12 text-center text-sm text-muted">
              <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare gli utenti.
            </div>
          ) : loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="cv-panel p-3.5">
                <div className="skeleton h-[72px] w-full rounded bg-line-soft" />
              </div>
            ))
          ) : rows.length === 0 ? (
            <div className="cv-panel px-4 py-12 text-center text-sm text-muted">Nessun utente trovato.</div>
          ) : (
            <>
              {rows.map((row) => (
                <MemberCard
                  key={row.id}
                  row={row}
                  checked={selected.has(row.id)}
                  onToggle={() => toggleRow(row.id)}
                  onOpen={() => setDetail(row)}
                />
              ))}
              <div className="cv-panel">
                <Pager
                  page={page}
                  maxPage={maxPage}
                  total={total}
                  from={from}
                  to={to}
                  rowsInPage={rows.length}
                  filtering={false}
                  busy={busy}
                  onPrev={() => setPage((p) => Math.max(0, p - 1))}
                  onNext={() => setPage((p) => Math.min(maxPage, p + 1))}
                />
              </div>
            </>
          )}
        </div>

        {/* wide (tablet+ / laptop / desktop): table */}
        <div className="mbr-table-wrap cv-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead>
                <tr className="border-b border-line bg-card-2">
                  <th className="w-9 px-4 py-[11px]">
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} className="accent-verdigris" />
                  </th>
                  <th className="px-4 py-[11px] text-left text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                    Utente
                  </th>
                  <th className="mbr-country px-4 py-[11px] text-left text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                    Paese
                  </th>
                  <th className="mbr-plate px-4 py-[11px] text-left text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                    Targa
                  </th>
                  <th className="mbr-via px-4 py-[11px] text-left text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                    Iscrizione
                  </th>
                  <th className="mbr-consent px-4 py-[11px] text-left text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                    Marketing
                  </th>
                  <th className="mbr-date px-4 py-[11px] text-left text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                    Registrato
                  </th>
                  <th className="px-4 py-[11px] text-left text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                    Stato
                  </th>
                  <th className="px-4 py-[11px]" />
                </tr>
              </thead>
              <tbody>
                {isError ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-14 text-center text-sm text-muted">
                      <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare gli utenti.
                    </td>
                  </tr>
                ) : loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} className="border-b border-line-soft">
                      <td colSpan={9} className="px-4 py-[13px]">
                        <div className="skeleton h-4 w-full rounded bg-line-soft" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-14 text-center text-sm text-muted">
                      Nessun utente trovato.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const el = row.elements as El
                    const via = regVia(el)
                    return (
                      <tr
                        key={row.id}
                        onClick={() => setDetail(row)}
                        className={cn(
                          'cursor-pointer border-b border-line-soft last:border-0 hover:bg-paper',
                          selected.has(row.id) && 'bg-verdigris-soft/40',
                        )}
                      >
                        <td className="px-4 py-[13px]" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selected.has(row.id)}
                            onChange={() => toggleRow(row.id)}
                            className="accent-verdigris"
                          />
                        </td>
                        <td className="px-4 py-[11px]">
                          <div className="flex items-center gap-3">
                            <Avatar name={el.name} last={el.lastName} />
                            <div className="min-w-0">
                              <div className="font-semibold text-coal">
                                {String(el.name ?? '')} {String(el.lastName ?? '')}
                              </div>
                              <div className="mt-0.5 truncate text-[12px] text-muted">
                                {String(el.contactEmail ?? '')}
                                {el.phoneNumber ? ` · ${String(el.phoneNumber)}` : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="mbr-country whitespace-nowrap px-4 py-[13px]">
                          <CountryCell country={el.country} />
                        </td>
                        <td className="mbr-plate px-4 py-[13px]">
                          {el.licencePlate ? (
                            <span className="rounded-md border border-line bg-paper px-2 py-0.5 font-mono text-[12px] font-semibold tracking-wider text-coal">
                              {String(el.licencePlate)}
                            </span>
                          ) : (
                            <span className="text-faint">—</span>
                          )}
                        </td>
                        <td className="mbr-via px-4 py-[13px]">
                          <span className="rounded-md border border-line bg-paper px-2 py-[3px] text-[11px] font-semibold text-slate">
                            {via}
                          </span>
                        </td>
                        <td className="mbr-consent px-4 py-[13px]">
                          <ConsentBadge ok={isYes(el.commercialPrivacy)} />
                        </td>
                        <td className="mbr-date whitespace-nowrap px-4 py-[13px] font-mono text-[12.5px] text-slate">
                          {entityDate(row.creationDate)}
                        </td>
                        <td className="px-4 py-[13px]">
                          <StatusChip enabled={isYes(el.enabled)} />
                        </td>
                        <td className="px-4 py-[13px] text-right">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setDetail(row)
                            }}
                            className="rounded-md border border-line bg-card px-2.5 py-1 text-[12px] font-semibold text-slate hover:border-faint"
                          >
                            Profilo
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* footer */}
          <div className="border-t border-line-soft">
            <Pager
              page={page}
              maxPage={maxPage}
              total={total}
              from={from}
              to={to}
              rowsInPage={rows.length}
              filtering={false}
              busy={busy}
              onPrev={() => setPage((p) => Math.max(0, p - 1))}
              onNext={() => setPage((p) => Math.min(maxPage, p + 1))}
            />
          </div>
        </div>
      </div>

      <MemberDrawer row={detail} onClose={() => setDetail(null)} />
    </div>
  )
}
